import Decimal from "decimal.js";
import { z } from "zod";

/**
 * Duck-type check for decimal.js Decimal instances.
 * Checks for characteristic methods that decimal.js Decimals have.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isDecimalJs(v: any): v is Decimal {
  return (
    v &&
    typeof v === "object" &&
    typeof v.toString === "function" &&
    typeof v.toNumber === "function" &&
    typeof v.isFinite === "function" &&
    typeof v.isInteger === "function" &&
    typeof v.add === "function" &&
    typeof v.sub === "function" &&
    typeof v.mul === "function" &&
    typeof v.div === "function" &&
    typeof v.equals === "function"
  );
}

/**
 * Duck-type check for Prisma.Decimal instances.
 * Prisma.Decimal has toString/toNumber but lacks decimal.js methods like isFinite/add.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isPrismaDecimal(v: any): boolean {
  return (
    v &&
    typeof v === "object" &&
    typeof v.toString === "function" &&
    typeof v.toNumber === "function" &&
    !("isFinite" in v) && // Prisma.Decimal doesn't have isFinite
    !("add" in v) // Prisma.Decimal doesn't have add
  );
}

/**
 * Shared Zod schema for validating and converting to Decimal instances
 * Accepts string, number, decimal.js Decimal, or Prisma.Decimal inputs and ensures output is always a Decimal
 * Used across all hold-to-earn domain entities for decimal fields
 *
 * Follows the pattern: Use Prisma.Decimal in domain objects, avoid instanceof checks, use duck typing
 *
 * TODO: Consider simplifying this schema or improving error messages for better DX
 */
export const DecimalSchema = z
  .union([
    z.string().min(1, "Decimal string cannot be empty"),
    z.number(),
    z.custom<Decimal>((val): val is Decimal => isDecimalJs(val) || isPrismaDecimal(val), {
      message: "Invalid Decimal instance - must be string, number, decimal.js Decimal, or Prisma.Decimal",
    }),
  ])
  .transform((val, ctx): Decimal => {
    // Handle decimal.js Decimal instances - return as-is
    if (isDecimalJs(val)) {
      return val;
    }
    // Handle Prisma.Decimal by converting to string first
    if (isPrismaDecimal(val)) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return new Decimal((val as any).toString());
      } catch {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Invalid Prisma.Decimal value - could not convert to Decimal",
        });
        return z.NEVER;
      }
    }
    // Handle string and number inputs
    try {
      return new Decimal(val);
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Invalid decimal value - could not convert to Decimal",
      });
      return z.NEVER;
    }
  })
  .describe("Decimal"); // This is used to identify the field as a Decimal in the Zod schema
